import React, { useCallback, useMemo } from "react";
import { SiteTrafficPanelProps } from "./SiteTrafficPanel.types";
import { format, isToday } from "date-fns";
import { useRefreshRegister } from "../../components/RefreshProvider";
import useBreakpoints from "../../hooks/useBreakpoints";
import { usePostTrafficAwarenessGroupedQuery } from "../../app/useGeneratedApi";
import { OpenClickEventStatistics } from "../../app/GeneratedApi";
import { getLastPeriod } from "../../utils/dateUtils";

const formatMap: {
  [key in SiteTrafficPanelProps["groupTimeUnit"]]: string;
} = {
  HOUR: "hh:mm",
  DAY: "d MMM",
  MONTH: "MMM yy",
  YEAR: "yyyy",
};

export function useSiteTrafficPanel(props: SiteTrafficPanelProps) {
  const { from, to, groupTimeUnit } = props;
  const { mdUp } = useBreakpoints();

  const {
    data: trafficAwarenessGroupedData,
    refetch,
    isLoading: isLoadingGroupedStatistics,
    error,
  } = usePostTrafficAwarenessGroupedQuery({
    from,
    to,
    groupTimeUnit,
    eventTypes: ["UNIQUE_CLICK"],
  });

  useRefreshRegister(async () => {
    await refetch();
  });

  const statistics = useMemo(() => {
    return (
      trafficAwarenessGroupedData?.uniqueClickedGroupedStatistics || {
        statistics: [],
        comparePercent: undefined,
        currentTotal: 0,
      }
    );
  }, [trafficAwarenessGroupedData]);

  const xValueAccessor = useCallback((item: OpenClickEventStatistics) => {
    return item.eventTime!;
  }, []);

  const yValueAccessor = useCallback((item: OpenClickEventStatistics) => {
    return item.currentCount!;
  }, []);

  const formatDate = useCallback(
    (date: string | Date) => {
      const dateObj = typeof date === "string" ? new Date(date) : date;
      return format(dateObj, formatMap[groupTimeUnit] || "MMM d");
    },
    [groupTimeUnit]
  );

  return {
    statistics,
    yValueAccessor,
    xValueAccessor,
    formatDate,
    mdUp,
    isLoadingGroupedStatistics,
    error,
    groupTimeUnit,
    lastPeriod: isToday(to || new Date())
      ? getLastPeriod(trafficAwarenessGroupedData?.compareDays || 0)
      : "period",
  };
}
